import type { AtmosphereParams, SkyStop } from './types';
import type { LinearRGB } from '../core/color';

/**
 * TypeScript mirror of the GLSL atmosphere functions.
 * Used for testing and calibration. Must match shaders/atmosphere.glsl exactly.
 */

/**
 * Free views take the fitted exp²; the painter view's optical depth meets it at the calibration
 * point and rises as the painter exponent of distance, and the painter amount blends the two.
 */
export function atmosphereTransmittance(
  params: AtmosphereParams,
  distance: number,
  height: number,
  painterAmount = 0
): number {
  const heightTerm = Math.exp(-height * params.heightFalloff);
  let optical = Math.pow(params.density * distance, params.exponent);
  if (painterAmount > 0) {
    const calibration = params.calibrationDistanceM;
    const painter =
      Math.pow(params.density * calibration, params.exponent) *
      Math.pow(distance / calibration, params.painterExponent);
    optical = painterAmount >= 1 ? painter : optical + (painter - optical) * painterAmount;
  }
  return Math.exp(-(optical * heightTerm));
}

export function applyAtmosphere(
  params: AtmosphereParams,
  color: LinearRGB,
  distance: number,
  height: number,
  painterAmount = 0
): LinearRGB {
  const T = atmosphereTransmittance(params, distance, height, painterAmount);
  const fog = (1 - T) * params.maxOpacity;
  return [
    color[0] * (1 - fog) + params.hazeColor[0] * fog,
    color[1] * (1 - fog) + params.hazeColor[1] * fog,
    color[2] * (1 - fog) + params.hazeColor[2] * fog,
  ];
}

export function skyColor(
  stops: readonly SkyStop[],
  hazeColor: LinearRGB,
  elevationDeg: number
): LinearRGB {
  if (stops.length === 0) {
    return hazeColor;
  }

  const firstStop = stops[0]!;
  if (elevationDeg <= firstStop.elevationDeg) {
    return hazeColor;
  }

  for (let i = 1; i < stops.length; i++) {
    const currStop = stops[i]!;
    const prevStop = stops[i - 1]!;
    if (elevationDeg <= currStop.elevationDeg) {
      const t =
        (elevationDeg - prevStop.elevationDeg) / (currStop.elevationDeg - prevStop.elevationDeg);
      const c0 = prevStop.color;
      const c1 = currStop.color;
      return [
        c0[0] * (1 - t) + c1[0] * t,
        c0[1] * (1 - t) + c1[1] * t,
        c0[2] * (1 - t) + c1[2] * t,
      ];
    }
  }

  const lastStop = stops[stops.length - 1]!;
  return lastStop.color;
}

export function hazeFraction(
  params: AtmosphereParams,
  distance: number,
  height: number,
  painterAmount = 0
): number {
  const T = atmosphereTransmittance(params, distance, height, painterAmount);
  return (1 - T) * params.maxOpacity;
}
